import React from "react";

import Button, { LinkButton } from "@atlaskit/button-next/new";
import Heading from "@atlaskit/heading";
import ArrowUpRightIcon from "@atlaskit/icon/core/arrow-up-right";
import { Flex, Stack, Text, xcss } from "@atlaskit/primitives-next";

import { useFireAnalyticsEvents } from "../../../../analytics";
import {
    ActionSubjectIdKey,
    ActionSubjectKey,
    ActionKey,
    SourceKey
} from "../../../../analytics/constants";

interface ResourceCardProps {
    title: string;
    description: string;
    isHighlighted?: boolean;
    buttonText: string;
    href?: string;
    onClick?: () => void;
    subject?: string;
}

const ResourceCardFooterStyles = xcss({
    justifyContent: "flex-end",
    paddingBlockEnd: "space.250"
});

const ResourceCardContentStyles = xcss({
    paddingBlock: "space.200"
});

export const ResourceCard: React.FC<ResourceCardProps> = ({
    title,
    description,
    buttonText,
    href,
    isHighlighted = false,
    onClick,
    subject
}) => {
    const { fireUIEvent } = useFireAnalyticsEvents();

    const ResourceCardStyles = xcss({
        paddingInline: "space.250",
        borderRadius: "border.radius.100",
        borderColor: "color.border",
        borderWidth: "border.width",
        borderStyle: "solid",
        backgroundColor: isHighlighted
            ? "color.background.accent.blue.subtlest"
            : "color.background.neutral.subtle"
    });

    const handleOnClickButton = () => {
        fireUIEvent({
            source: SourceKey.FASTSHIFT_PORTAL_DASHBOARD_SCREEN,
            action: ActionKey.CLICKED,
            actionSubject: ActionSubjectKey.BUTTON,
            actionSubjectId:
                ActionSubjectIdKey.REQUEST_FASTSHIFT_ASSISTANCE_CARD,
            attributes: {}
        });

        if (onClick) {
            onClick();
        }
    };

    const handleOnClickLink = () => {
        fireUIEvent({
            source: SourceKey.FASTSHIFT_PORTAL_DASHBOARD_SCREEN,
            action: ActionKey.CLICKED,
            actionSubject: ActionSubjectKey.LINK,
            actionSubjectId:
                ActionSubjectIdKey.FASTSHIFT_RESOURCES_CARD_LINK_CLICK,
            attributes: {
                subject
            }
        });
    };

    return (
        <Stack xcss={ResourceCardStyles}>
            <article>
                <Stack
                    xcss={ResourceCardContentStyles}
                    space={isHighlighted ? "space.150" : "space.050"}
                >
                    <header>
                        <Heading as="h4">{title}</Heading>
                    </header>
                    <Text color="color.text.subtle">{description}</Text>
                </Stack>
                <footer>
                    <Flex xcss={ResourceCardFooterStyles}>
                        {href ? (
                            <LinkButton
                                href={href}
                                iconAfter={ArrowUpRightIcon}
                                appearance={
                                    isHighlighted ? "primary" : "default"
                                }
                                target="_blank"
                                onClick={handleOnClickLink}
                            >
                                {buttonText}
                            </LinkButton>
                        ) : (
                            <Button
                                onClick={handleOnClickButton}
                                iconAfter={ArrowUpRightIcon}
                                appearance={
                                    isHighlighted ? "primary" : "default"
                                }
                            >
                                {buttonText}
                            </Button>
                        )}
                    </Flex>
                </footer>
            </article>
        </Stack>
    );
};
